import Layer from '@arcgis/core/layers/Layer';
import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer';
import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const cameraDefaultUrl: string = require('./camera_default.svg');

/**
 * Extends the 4.23 typings with properties that exist at runtime but are
 * missing from the bundled @arcgis/core type declarations.
 */
interface ClusterFeatureReduction
  extends __esri.FeatureReductionClusterProperties {
  maxScale?: number;
  labelsVisible?: boolean;
}

/** ~1:18,000 — break clusters when zoomed in to neighborhood / street level */
const CLUSTER_MAX_SCALE_NEIGHBORHOOD = 18000;

/**
 * Esri point clustering for dense camera layers:
 * - cluster radius: large (120px) so nearby points group at city/region zoom
 * - symbol size: low–medium (42px–62px) cluster markers
 * - maxScale: clustering turns off when zoomed in past neighborhood scale
 */
export function createCameraClusterFeatureReduction(): ClusterFeatureReduction {
  return {
    type: 'cluster',
    clusterRadius: '120px',
    clusterMinSize: '42px',
    clusterMaxSize: '62px',
    maxScale: CLUSTER_MAX_SCALE_NEIGHBORHOOD,
    labelsVisible: true,
    labelingInfo: [
      {
        labelExpressionInfo: {
          expression: '$feature.cluster_count',
        },
        deconflictionStrategy: 'none' as const,
        labelPlacement:
          'center-center' as __esri.LabelClassProperties['labelPlacement'],
        symbol: {
          type: 'text',
          color: [255, 255, 255, 1],
          haloColor: [40, 40, 40, 0.85],
          haloSize: 1.5,
          xoffset: 10,
          yoffset: 10,
          font: {
            size: 10,
            weight: 'bold',
          },
        },
      },
    ],
  };
}

/**
 * Creates a SimpleRenderer using the shared camera_default.svg icon as a
 * PictureMarkerSymbol, suitable for point FeatureLayers.
 */
export function createCameraRenderer() {
  return new SimpleRenderer({
    symbol: new PictureMarkerSymbol({
      url: cameraDefaultUrl,
      width: '28px',
      height: '28px',
    }),
  });
}

/**
 * If the layer title contains "camera" (case-insensitive), set opacity to 0.8.
 * For FeatureLayers, also apply the camera_default.svg point renderer.
 */
export function applyCameraRenderer(layer: Layer): void {
  if (!layer.title?.toLowerCase().includes('camera')) {
    return;
  }

  layer.opacity = 1;

  if (layer.type === 'feature') {
    const featureLayer = layer as __esri.FeatureLayer;
    featureLayer.renderer = createCameraRenderer();
    featureLayer.featureReduction =
      createCameraClusterFeatureReduction() as unknown as __esri.FeatureReductionCluster;
  }
}
